import { DataTypes, TypeSchema } from './common.datatypes';
import { GroupResourceOption, TourRoleTypes } from './groups.datatypes';
import { PaymentSchedule } from './payment_schedule.datatypes';
import { WcvDocument } from './documents.datatypes';
import {CreditCardFee} from './payments.datatypes';

export interface ResourceOptionCondition {
    __typename: string | undefined,
    id?: number
    resource_option_id?: number
    type?: string
    attribute?: string
    attribute_type?: string
    attribute_value?: string | Date
    condition?: string
}

export interface ResourceOption {
    __typename: string | undefined,
    id?: number,
    resource_id?: number,
    internal_name?: string,
    display_name?: string,
    description?: string,
    disabled?: boolean,
    hidden?: boolean,
    additional_charge?: boolean,
    target?: number,
    action_type?: number,
    action_value?: string | null,
    editing_level?: number,
    view_level?: number,
    is_admin_only?: boolean,

    resource?: Resource,
    groupResources?: GroupResourceOption[],
    conditions?: ResourceOptionCondition[]
}

export interface ResourceBank {
    __typename?: string | undefined,
    id?: number
    resource_id?: number | undefined
    capacity: number
    warning_count: number
    unit_price: number
    date?: Date
    remaining?: number

    resource?: Resource
}

export interface ResourceTourRole {
    __typename?: string | undefined,
    id: number
    tour_resource_id: number
    tour_role: number
    age_lower_bound: number
    age_upper_bound: number

    resource?: Resource
}

export interface Resource {
    __typename: string | undefined,
    id: number
    parent_resource_id?: number
    internal_name: string
    display_name: string
    t_and_c_url: string
    disabled?: boolean
    resource_type?: number
    has_price?: boolean
    has_dates?: boolean
    consumable?: boolean
    available_from?: Date | string
    available_to?: Date | string
    allow_manual_overbooking?: boolean
    show_on_invoice?: boolean
    description?: string
    locked?: boolean
    comms_email_address?: string
    max_additional_nights?: number
    max_nights_before?: number
    max_nights_after?: number
    guest_capacity?: number
    invoice_document_id?: number
    netsuite_tour_type?: string
    booking_tool_edit_cutoff?: Date | string

    image_logo_url?: string
    image_banner_url?: string
    allow_multiple?: boolean
    rain_date_note?: string
    cancellation_policy?: string
    terms_and_conditions?: string
    contact_us_message?: string

    use_room_bank?: boolean
    room_bank_resource_id?: number
    room_bank?: Resource
    roomResources?: Resource[]

    parent?: Resource
    resources?: Resource[]
    resource_banks?: ResourceBank[]
    options?: ResourceOption[]
    tour_roles?: ResourceTourRole[]
    payment_schedule?: PaymentSchedule
    documents?: WcvDocument[],
    credit_card_fee?: CreditCardFee
}

export const ResourceType = {
    TOUR: 0,
    HOTEL: 1,
    ROOM: 2,
    COSTUME: 3,
    SHOES: 4,
    GENERAL: 5,
    //EVENT: 6,
    SEASON: 7,
    ROOM_BANK: 8,
    FOLDER: 9,
};

export const ResourceTypes = [
    {name: 'tour'},
    {name: 'hotel'},
    {name: 'room'},
    {name: 'costume'},
    {name: 'shoes'},
    {name: 'general'},
    null,
    {name: 'season'},
    {name: 'room bank'},
    {name: 'folder'},
];

export const ResourceOptionTarget = {
    TRAVELLERS: 0,
    ROOMS: 1,
};

export const ResourceOptionTargets = [
    {name: 'Each Traveler'},
    {name: 'Each Room'},
];

export const ResourceOptionAction = {
    ROOM_RESOURCE_AVAILABLE: 0,
    TRAVELLER_RESOURCE_AVAILABLE: 1,
    ROOM_RESOURCE_MANDATORY: 2,
    TRAVELLER_RESOURCE_MANDATORY: 3,
    ADD_COST_TO_TRAVELLER: 4,
    ADD_COST_TO_TRAVELLER_PER_NIGHT: 5,
};

export const ResourceOptionActions = [
    {
        name: 'Make Resource Available to Room'
    },
    {
        name: 'Make Resource Available to Traveller'
    },
    {
        name: 'Make Resource Mandatory for Room',
    },
    {
        name: 'Make Resource Mandatory for Traveller',
    },
    {
        name: 'Add Cost/Discount To Traveller',
        value: {
            type: DataTypes.NUMBER,
            defaultValue: '0',
        }
    },
    {
        name: 'Add Cost/Discount To Traveller for Night',
        value: {
            type: DataTypes.NUMBER,
            defaultValue: '0',
        }
    },
];

export const ResourceOptionConditionType = {
    TRAVELLER: 'traveller',
    ROOM: 'room',
    BOOKING: 'booking',
    ROOM_PARTY_NIGHT: 'room_party_night',
    ADMIN: 'admin',
};

export const ResourceOptionConditionTypes: { [key: string]: TypeSchema } = {
    'traveller': {
        value: 'traveller',
        label: 'Traveler',
        atts: [
            // {
            //   value: 'nationality',
            //   label: 'Nationality',
            //   type: DataTypes.STRING,
            //   defaultValue: 'usa',
            //   options: [
            //     {
            //       label: 'USA',
            //       value: 'usa',
            //     },
            //     {
            //       label: 'Canada',
            //       value: 'canada',
            //     },
            //   ],
            // },
            {
                value: 'age',
                label: 'Age at Event',
                type: DataTypes.NUMBER,
            },
            {
                value: 'tour_role',
                label: 'Tour Role',
                type: DataTypes.NUMBER,
                options: TourRoleTypes.map((tourRole, i) => ({value: '' + i, label: tourRole.name})),
            },
            // {
            //   value: 'gender',
            //   label: 'Gender',
            //   type: DataTypes.STRING,
            //   options: [
            //     {
            //       label: 'female',
            //       value: 'female',
            //     },
            //     {
            //       label: 'male',
            //       value: 'male',
            //     },
            //     {
            //       label: 'prefer not to say',
            //       value: 'na',
            //     },
            //   ]
            // },
            // {
            //   value: 'name',
            //   label: 'Name',
            //   type: DataTypes.STRING,
            // },
            // {
            //   value: 'shoe_category',
            //   label: 'Shoe Category',
            //   type: DataTypes.STRING,
            // },
            // {
            //   value: 'shoe_size',
            //   label: 'Shoe Size',
            //   type: DataTypes.NUMBER,
            // },
            // {
            //   value: 'pants_size',
            //   label: 'Pants Size',
            //   type: DataTypes.STRING,
            // },
            // {
            //   value: 'shirt_size',
            //   label: 'Shirt Size',
            //   type: DataTypes.STRING,
            // },
            // {
            //   value: 'tie_size',
            //   label: 'Tie Size',
            //   type: DataTypes.STRING,
            // },
            {
                value: 'has_option_with_name',
                label: 'Has option with name',
                type: DataTypes.STRING,
            },
            {
                value: 'has_no_option_with_name',
                label: 'Has no option with name',
                type: DataTypes.STRING,
            },
            {
                value: 'room_guest_number',
                label: 'Room Guest Number',
                type: DataTypes.NUMBER,
            },
            {
                value: 'room_adult_number',
                label: 'Room Adult Number',
                type: DataTypes.NUMBER,
            },
        ]
    },
    'room': {
        label: 'Room',
        value: 'room',
        atts: [
            {
                value: 'num_room_occupants',
                label: '# occupants',
                type: DataTypes.NUMBER,
            },
            {
                value: 'start_date',
                label: 'Start Date',
                type: DataTypes.DATE,
            },
            {
                value: 'end_date',
                label: 'End Date',
                type: DataTypes.DATE,
            },
            {
                value: 'num_days',
                label: '# Days',
                type: DataTypes.NUMBER,
            },
            {
                value: 'has_option_with_name',
                label: 'Has option with name',
                type: DataTypes.STRING,
            },
            {
                value: 'has_no_option_with_name',
                label: 'Has no option with name',
                type: DataTypes.STRING,
            },
            {
                value: 'hotel_name',
                label: 'Hotel Name',
                type: DataTypes.STRING,
            },
            {
                value: 'has_occupant_option_with_name',
                label: 'Has Occupant option with name',
                type: DataTypes.STRING,
            },
            {
                value: 'has_no_occupant_option_with_name',
                label: 'Has no Occupant option with name',
                type: DataTypes.STRING,
            },
        ],
    },
    'booking': {
        value: 'booking',
        label: 'Booking',
        atts: [
            {
                value: 'num_travellers',
                label: '# travellers',
                type: DataTypes.NUMBER,
            },
            {
                value: 'start_date',
                label: 'Start Date',
                type: DataTypes.DATE,
            },
            {
                value: 'end_date',
                label: 'End Date',
                type: DataTypes.DATE,
            },
            {
                value: 'num_days',
                label: '# Days',
                type: DataTypes.NUMBER,
            },
        ]
    },
    'room_party_night': {
        label: 'Room Night',
        value: 'room_party_night',
        atts: [
            {
                value: 'num_room_occupants',
                label: '# occupants',
                type: DataTypes.NUMBER,
            },
            {
                value: 'extra_night',
                label: 'is Extra Night',
                type: DataTypes.BOOLEAN,
            },
        ],
    },
    'admin': {
        label: 'Admin',
        value: 'admin',
        atts: [
            {
                value: 'is_admin_only',
                label: 'admin only?',
                type: DataTypes.BOOLEAN,
            },
        ],
    },
};
